import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from '../DataTable/DataTable';
import { fetchVariants, createVariant, editVariant, removeVariant } from '../../redux/services/variantsService';
import { fetchProducts } from '../../redux/services/productsService';
import { confirmDelete } from '../../utils/sweetalert';
import { hasPermission } from '../../utils/authHelper';
import AccessDenied from '../AccessDenied';
import VariantCard, { getProductVariantSchema, normalizeGstRate } from '../Products/VariantCard';
import { renderVariantLabel } from '../Products/VariantBuilder';
import { getImageUrl, toRelativePath } from '../../utils/imageUrl';

const getImgSrc = getImageUrl;

const KM = {
  orange: 'var(--warning-600)', orangeLight: 'var(--warning-50)', blue: 'var(--primary-600)',
  green: 'var(--success-main)', teal: 'var(--info-500)', border: 'var(--border-color)',
  text: 'var(--text-primary-light)', muted: 'var(--neutral-500)', bg: 'var(--bg-color)',
};

function safeAttrs(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
  }
  return [];
}

const getVariantSignature = (attributes) => {
  const parsed = safeAttrs(attributes);
  return parsed
    .filter(a => a.key && a.value !== undefined && a.value !== null && String(a.value).trim() !== '')
    .map(a => `${a.key.trim().toLowerCase()}:${a.value.trim().toLowerCase()}`)
    .sort()
    .join(';');
};

// Product Image Dimension Validator (1000x1000px 1:1 Square)
const PRODUCT_IMAGE_DIMENSIONS = {
  width: 1000,
  height: 1000,
  aspectRatio: 1,
  tolerance: 0.05,
  maxFileSize: 3 * 1024 * 1024,
  formats: [
    'image/jpeg', 'image/png', 'image/webp', 'image/gif',
    'image/svg+xml', 'image/bmp', 'image/tiff', 'image/x-icon',
    'image/heic', 'image/heif', 'image/avif'
  ],
};

const validateProductImageDimensions = (file) => {
  return new Promise((resolve) => {
    if (file.size > PRODUCT_IMAGE_DIMENSIONS.maxFileSize) {
      resolve({ valid: false, error: `File too large. Max: 3MB. You have: ${(file.size / 1024 / 1024).toFixed(2)}MB` });
      return;
    }
    if (!PRODUCT_IMAGE_DIMENSIONS.formats.includes(file.type)) {
      resolve({ valid: false, error: `Invalid format. Use common image formats (JPG, PNG, WebP, GIF, SVG, BMP, TIFF, ICO, HEIC, HEIF, AVIF). You have: ${file.type || 'unknown'}` });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
         const { width, height } = img;
         const actualRatio = width / height;
         const expectedRatio = PRODUCT_IMAGE_DIMENSIONS.aspectRatio;
         const ratioDiff = Math.abs(actualRatio - expectedRatio) / expectedRatio;
         // if (ratioDiff > PRODUCT_IMAGE_DIMENSIONS.tolerance) {
         //   resolve({
         //     valid: false,
         //     error: `Incorrect aspect ratio. Use 1:1 square (${PRODUCT_IMAGE_DIMENSIONS.width}×${PRODUCT_IMAGE_DIMENSIONS.height}px). Yours: ${width}×${height}px`,
         //     dimensions: { width, height },
         //   });
         //   return;
         // }
         resolve({
           valid: true,
           dimensions: { width, height },
         });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

// Convert a saved variant row → VariantBuilder SKU shape
function variantToSku(v) {
  const parseVariantName = (name) => {
    if (!name || name === 'Default') return [];
    let customNoteCount = 0;
    return name.split(/\s*(?:·|\||,|\/|-)\s*/).map(part => {
      const ci = part.indexOf(':');
      if (ci === -1) {
        customNoteCount++;
        const key = customNoteCount === 1 ? 'Custom Note' : `Custom Note ${customNoteCount}`;
        return { key, value: part.trim(), customValue: '' };
      }
      return { key: part.slice(0, ci).trim(), value: part.slice(ci + 1).trim(), customValue: '' };
    }).filter(a => a.key && a.value);
  };

  const nameAttrs = parseVariantName(v.variantName);
  const rawAttrs = v.attributes;
  const parsedAttrs = safeAttrs(rawAttrs);

  // HEAL LOGIC: If parsed attributes from variantName do not match DB attributes,
  // automatically heal DB attributes to match the name-based ones.
  let finalAttrs = parsedAttrs;
  if (nameAttrs.length > 0) {
    const matches = nameAttrs.every(na =>
      parsedAttrs.some(pa =>
        na.key.toLowerCase() === pa.key.toLowerCase() &&
        na.value.toLowerCase() === pa.value.toLowerCase()
      )
    );
    if (!matches || parsedAttrs.length !== nameAttrs.length) {
      finalAttrs = nameAttrs;
    }
  }

  const combo = finalAttrs
    .filter(a => a.key && a.value && !a.key.startsWith('Custom Note'))
    .map(a => ({ key: a.key, value: a.value }));

  let dims = v.shippingDimensions;
  if (typeof dims === 'string') {
    try { dims = JSON.parse(dims); } catch (e) { dims = {}; }
  } else {
    dims = dims || {};
  }

  return {
    id: v.id,
    variantName: v.variantName || combo.map(c => `${c.key}: ${c.value}`).join(' · ') || 'Default',
    combo,
    attributes:   finalAttrs,
    mrp:          String(v.mrp         || ''),
    salesPrice:   String(v.salesPrice  || ''),
    stock:        String(v.stock       || ''),
    lowStockThreshold: String(v.lowStockThreshold || '10'),
    sku:          v.sku || '',
    gstMode:      v.gstMode || 'Inclusive',
    gstRate:      normalizeGstRate(v.gstRate),
    status:       v.status || 'Active',
    galleryPreviews: (() => {
      let imgs = [];
      if (Array.isArray(v.images)) {
        imgs = v.images;
      } else if (typeof v.images === 'string') {
        try { imgs = JSON.parse(v.images); } catch(e) { imgs = []; }
      }
      if ((!imgs || imgs.length === 0) && v.image) {
        imgs = [v.image];
      } else if (v.image && !imgs.some(img => img === v.image || getImgSrc(img) === getImgSrc(v.image))) {
        imgs = [v.image, ...imgs];
      }
      return (imgs || []).filter(Boolean).map(img => {
        const rawPath = typeof img === 'string' ? img : (img?.path || img?.url || '');
        return {
          url: getImgSrc(rawPath),
          path: toRelativePath(rawPath) || (typeof rawPath === 'string' ? rawPath : ''),
          file: null
        };
      });
    })(),
    imageFile:    null,
    imagePreview: v.image ? getImgSrc(v.image) : null,
    shippingWeight: v.shippingWeight ? String(v.shippingWeight) : '',
    shippingLength: dims.length ? String(dims.length) : '',
    shippingBreadth: dims.breadth ? String(dims.breadth) : '',
    shippingHeight: dims.height ? String(dims.height) : '',
  };
}

const formCard   = { background: '#fff', border: `1px solid ${KM.border}`, borderRadius: 12, marginBottom: 20 };
const formHeader = { background: KM.blue, padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 10, borderTopLeftRadius: 11, borderTopRightRadius: 11 };
const headerIcon = { width: 32, height: 32, background: 'rgba(255,255,255,0.15)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 15, fontWeight: 600 };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 5 };
const labelStyle = { fontSize: 11, fontWeight: 600, color: KM.muted, textTransform: 'uppercase', letterSpacing: '0.05em' };
const inputStyle = { padding: '9px 12px', border: `1px solid ${KM.border}`, borderRadius: 8, fontSize: 13, color: KM.text, background: '#fff', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const errorStyle = { fontSize: 11, color: '#dc2626', fontWeight: 600, marginTop: 2 };
const submitBtn  = { padding: '12px 0', background: KM.orange, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: '100%', marginTop: 8 };

export default function Variants({ showToast }) {
  const dispatch = useDispatch();
  const { items: rows, loading } = useSelector(state => state.variants);
  const { items: products }      = useSelector(state => state.products);
  const { settings: invSettings } = useSelector(state => state.notifications);

  const stockColor = (qty) => {
    const high   = invSettings?.highStockThreshold   ?? 51;
    const medium = invSettings?.mediumStockThreshold  ?? 11;
    if (qty === 0)    return KM.red;
    if (qty < medium) return KM.red;
    if (qty < high)   return '#F59E0B';
    return KM.green;
  };

  // mode: 'none' | 'add' | 'edit'
  const [mode,          setMode]          = useState(() => localStorage.getItem('draft_mode') || 'none');
  const [editingId,     setEditingId]     = useState(null);
  const [productId,     setProductId]     = useState(() => localStorage.getItem('draft_productId') || '');
  const [filterProduct, setFilterProduct] = useState('');
  const [errors,        setErrors]        = useState({});

  // For ADD — loads draft if exists
  const [skus, setSkus] = useState(() => {
    const draft = localStorage.getItem('draft_skus');
    if (draft) {
      try { return JSON.parse(draft); } catch { return []; }
    }
    return [];
  });

  // For EDIT — single SKU loaded from the row being edited
  const [editSku,  setEditSku]  = useState(null);

  useEffect(() => {
    dispatch(fetchVariants());
    dispatch(fetchProducts());
  }, [dispatch]);

  // Persist draft mode, productId, and skus
  useEffect(() => {
    if (mode) {
      localStorage.setItem('draft_mode', mode);
    } else {
      localStorage.removeItem('draft_mode');
    }
  }, [mode]);

  useEffect(() => {
    if (productId) {
      localStorage.setItem('draft_productId', productId);
    } else {
      localStorage.removeItem('draft_productId');
    }
  }, [productId]);

  useEffect(() => {
    if (skus && skus.length > 0) {
      const clean = skus.map(s => {
        const { imageFile, galleryFiles, galleryPreviews, ...rest } = s;
        const cleanPreviews = (galleryPreviews || []).map(p => {
          if (p && typeof p === 'object' && p.file) {
            return { url: p.url, isNew: p.isNew }; // omit File
          }
          return p;
        });
        return { ...rest, galleryPreviews: cleanPreviews };
      });
      localStorage.setItem('draft_skus', JSON.stringify(clean));
    } else {
      localStorage.removeItem('draft_skus');
    }
  }, [skus]);

  // ── Reset skus when productId changes in add mode ─────────────────────────
  useEffect(() => {
    if (mode !== 'add') return;
    if (!productId) {
      setSkus([]);
      return;
    }

    const draftProductId = localStorage.getItem('draft_productId');
    const draftSkus = localStorage.getItem('draft_skus');
    if (draftProductId === productId && draftSkus) {
      return;
    }

    const product = products.find(p => String(p.id) === String(productId));
    const schema = getProductVariantSchema(product);
    const initialAttrs = [];
    schema.forEach(field => {
      if (field.type === 'color') {
        initialAttrs.push({ key: field.key, value: '' });
        initialAttrs.push({ key: 'ColourHex', value: '#000000' });
      } else {
        initialAttrs.push({ key: field.key, value: '' });
      }
    });

    let pDims = product?.shippingDimensions;
    if (typeof pDims === 'string') {
      try { pDims = JSON.parse(pDims); } catch (e) { pDims = {}; }
    } else {
      pDims = pDims || {};
    }

    setSkus([{
      id: `new_${Date.now()}_0`,
      mrp: '',
      salesPrice: '',
      stock: '',
      status: 'Active',
      sku: '',
      attributes: initialAttrs,
      gstMode: 'Inclusive',
      gstRate: '0%',
      imageFile: null,
      imagePreview: null,
      galleryFiles: [],
      galleryPreviews: [],
      shippingWeight: product?.shippingWeight ? String(product.shippingWeight) : '',
      shippingLength: pDims?.length ? String(pDims.length) : '',
      shippingBreadth: pDims?.breadth ? String(pDims.breadth) : '',
      shippingHeight: pDims?.height ? String(pDims.height) : '',
    }]);
  }, [productId, mode, products]);

  const handleAddAnotherSku = () => {
    const product = products.find(p => String(p.id) === String(productId));
    const schema = getProductVariantSchema(product);
    const initialAttrs = [];
    schema.forEach(field => {
      if (field.type === 'color') {
        initialAttrs.push({ key: field.key, value: '' });
        initialAttrs.push({ key: 'ColourHex', value: '#000000' });
      } else {
        initialAttrs.push({ key: field.key, value: '' });
      }
    });

    let pDims = product?.shippingDimensions;
    if (typeof pDims === 'string') {
      try { pDims = JSON.parse(pDims); } catch (e) { pDims = {}; }
    } else {
      pDims = pDims || {};
    }

    setSkus(prev => [...prev, {
      id: `new_${Date.now()}_${prev.length}`,
      mrp: '',
      salesPrice: '',
      stock: '',
      status: 'Active',
      sku: '',
      attributes: initialAttrs,
      gstMode: 'Inclusive',
      gstRate: '0%',
      imageFile: null,
      imagePreview: null,
      galleryFiles: [],
      galleryPreviews: [],
      shippingWeight: product?.shippingWeight ? String(product.shippingWeight) : '',
      shippingLength: pDims?.length ? String(pDims.length) : '',
      shippingBreadth: pDims?.breadth ? String(pDims.breadth) : '',
      shippingHeight: pDims?.height ? String(pDims.height) : '',
    }]);
  };

  const copyProductShippingDetails = (skuTarget, setSkuTarget) => {
    const selectedProd = products.find(p => String(p.id) === String(productId));
    if (!selectedProd) {
      showToast.error('Please select a product first');
      return;
    }
    let pDims = selectedProd.shippingDimensions;
    if (typeof pDims === 'string') {
      try { pDims = JSON.parse(pDims); } catch (e) { pDims = {}; }
    } else {
      pDims = pDims || {};
    }
    const weight = selectedProd.shippingWeight ? String(selectedProd.shippingWeight) : '';
    const length = pDims?.length ? String(pDims.length) : '';
    const breadth = pDims?.breadth ? String(pDims.breadth) : '';
    const height = pDims?.height ? String(pDims.height) : '';

    if (!weight && !length && !breadth && !height) {
      showToast.error('Selected product has no default shipping dimensions configured');
      return;
    }

    setSkuTarget({
      ...skuTarget,
      shippingWeight: weight,
      shippingLength: length,
      shippingBreadth: breadth,
      shippingHeight: height,
    });
    showToast.success('Shipping details copied from default product!');
  };


  if (!hasPermission('variants_view')) {
    return <AccessDenied moduleName="Variants" />;
  }



  // ── Open ADD form ─────────────────────────────────────────────────────────
  const openAdd = () => {
    setMode('add');
    setEditingId(null);

    const draftProductId = localStorage.getItem('draft_productId');
    const draftSkus = localStorage.getItem('draft_skus');
    if (draftProductId && draftSkus) {
      setProductId(draftProductId);
      try {
        setSkus(JSON.parse(draftSkus));
      } catch (e) {
        setProductId('');
        setSkus([{}]);
      }
    } else {
      setProductId('');
      setSkus([{}]);
    }

    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Open EDIT form ─────────────────────────────────────────────────────────
  const openEdit = (v) => {
    setMode('edit');
    setEditingId(v.id);
    setProductId(String(v.productId || ''));
    const sku = variantToSku(v);
    if (!sku.attributes || sku.attributes.length === 0) {
      sku.attributes = [{ key: '', value: '', customValue: '' }];
    }
    setEditSku(sku);
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeForm = () => {
    setMode('none');
    setEditingId(null);
    setSkus([]);
    setEditSku(null);
    setErrors({});
    localStorage.removeItem('draft_skus');
    localStorage.removeItem('draft_productId');
    localStorage.removeItem('draft_mode');
  };

  // ── Validate SKU rows ─────────────────────────────────────────────────────
  const validateSkuRows = (skuList) => {
    const selectedProduct = products.find(p => String(p.id) === String(productId));
    const schema = getProductVariantSchema(selectedProduct);

    return skuList.map((v) => {
      const messages = [];
      const mrp = Number(v.mrp);
      const salesPrice = Number(v.salesPrice);
      const stock = Number(v.stock);

      // Verify that all keys in the schema are present and filled
      schema.forEach(field => {
        const attrVal = v.attributes?.find(a => a.key === field.key)?.value;
        if (attrVal === undefined || attrVal === null || String(attrVal).trim() === '') {
          messages.push(`Enter value for ${field.key}`);
        }
      });

      if (!v.mrp) messages.push('Enter MRP');
      else if (Number.isNaN(mrp) || mrp <= 0) messages.push('MRP must be greater than 0');
      if (!v.salesPrice) messages.push('Enter sales price');
      else if (Number.isNaN(salesPrice) || salesPrice <= 0) messages.push('Sales price must be greater than 0');
      else if (!Number.isNaN(mrp) && mrp > 0 && salesPrice > mrp) messages.push('Sales price cannot be greater than MRP');
      if (v.stock === '' || v.stock === undefined || v.stock === null) messages.push('Enter stock');
      else if (Number.isNaN(stock) || stock < 0) messages.push('Stock cannot be negative');
      if (!v.galleryPreviews || v.galleryPreviews.length === 0) messages.push('Upload at least one image in the variant gallery');
      const firstGalleryError = Object.values(v.galleryErrors || {}).find(Boolean);
      if (firstGalleryError) {
        messages.push(firstGalleryError);
      }

      return messages;
    });
  };

  const validateAdd = () => {
    const next = {};
    if (!productId) next.productId = 'Please select a product';
    if (!skus.length) next.skus = 'Add at least one variant using the options above';
    else {
      const variantErrors = validateSkuRows(skus);
      if (variantErrors.some(list => list.length)) {
        next.variantErrors = variantErrors;
      }

      // Check duplicates within the builder list
      const signatures = skus.map(s => getVariantSignature(s.attributes));
      const duplicateIndices = [];
      signatures.forEach((sig, idx) => {
        if (sig && signatures.indexOf(sig) !== idx) {
          duplicateIndices.push(idx);
        }
      });
      if (duplicateIndices.length > 0) {
        next.general = 'You have duplicate variant options in the list.';
      }

      const skusInput = skus.map(s => String(s.sku || '').trim().toLowerCase()).filter(Boolean);
      const duplicateSkus = skusInput.filter((val, idx) => skusInput.indexOf(val) !== idx);
      if (duplicateSkus.length > 0) {
        next.general = (next.general ? next.general + ' ' : '') + 'Duplicate SKU codes found in the list.';
      }

      // Check duplicates against database existing variants
      const existingVariants = (rows || []).filter(r => String(r.productId) === String(productId));
      const existingSignatures = existingVariants.map(r => getVariantSignature(r.attributes));
      const conflicts = signatures.filter(sig => sig && existingSignatures.includes(sig));
      if (conflicts.length > 0) {
        next.general = (next.general ? next.general + ' ' : '') + 'One or more variant combinations already exist in the database for this product.';
      }

      const existingSkus = (rows || []).map(r => String(r.sku || '').trim().toLowerCase()).filter(Boolean);
      const skuConflicts = skusInput.filter(sku => existingSkus.includes(sku));
      if (skuConflicts.length > 0) {
        next.general = (next.general ? next.general + ' ' : '') + `SKU code "${skuConflicts[0]}" is already in use by another variant.`;
      }
    }
    setErrors(next);
    return { valid: !Object.keys(next).length, errors: next };
  };

  const validateEdit = () => {
    const next = {};
    if (!productId) next.productId = 'Please select a product';
    if (!editSku) return { valid: false, errors: next };

    const mrp = Number(editSku.mrp);
    const salesPrice = Number(editSku.salesPrice);
    const stock = Number(editSku.stock);

    const selectedProduct = products.find(p => String(p.id) === String(productId));
    const schema = getProductVariantSchema(selectedProduct);

    // Verify that all keys in the schema are present and filled
    schema.forEach(field => {
      const attrVal = editSku.attributes?.find(a => a.key === field.key)?.value;
      if (attrVal === undefined || attrVal === null || String(attrVal).trim() === '') {
        next[`attr_${field.key}`] = `Enter value for ${field.key}`;
      }
    });

    if (!editSku.mrp) next.mrp = 'Enter MRP';
    else if (Number.isNaN(mrp) || mrp <= 0) next.mrp = 'MRP must be greater than 0';

    if (!editSku.salesPrice) next.salesPrice = 'Enter sales price';
    else if (Number.isNaN(salesPrice) || salesPrice <= 0) next.salesPrice = 'Sales price must be greater than 0';
    else if (!Number.isNaN(mrp) && mrp > 0 && salesPrice > mrp) next.salesPrice = 'Sales price cannot be greater than MRP';

    if (editSku.stock === '' || editSku.stock === undefined || editSku.stock === null) next.stock = 'Enter stock';
    else if (Number.isNaN(stock) || stock < 0) next.stock = 'Stock cannot be negative';

    if (!editSku.galleryPreviews || editSku.galleryPreviews.length === 0) {
      next.gallery = 'Upload at least one image in the variant gallery';
    }
    const firstGalleryError = Object.values(editSku.galleryErrors || {}).find(Boolean);
    if (firstGalleryError) {
      next.gallery = firstGalleryError;
    }

    // Check duplicates against database existing variants (excluding itself)
    const existingVariants = (rows || []).filter(r => String(r.productId) === String(productId) && String(r.id) !== String(editSku.id));
    const existingSignatures = existingVariants.map(r => getVariantSignature(r.attributes));
    const editSignature = getVariantSignature(editSku.attributes);
    if (editSignature && existingSignatures.includes(editSignature)) {
      next.general = 'A variant with these options already exists for this product.';
    }

    const editSkuVal = String(editSku.sku || '').trim().toLowerCase();
    if (editSkuVal) {
      const existingSkus = (rows || [])
        .filter(r => String(r.id) !== String(editSku.id))
        .map(r => String(r.sku || '').trim().toLowerCase())
        .filter(Boolean);
      if (existingSkus.includes(editSkuVal)) {
        next.sku = `SKU code "${editSku.sku}" is already in use by another variant.`;
      }
    }

    setErrors(next);
    return { valid: !Object.keys(next).length, errors: next };
  };

  // ── Async image dimension validation ──────────────────────────────────────
  const validateImageDimensions = async (skuList) => {
    return true;
  };

  // ── Submit ADD ────────────────────────────────────────────────────────────
  // Simple create-only — no differential sync since add mode is always blank.
  // All skus in the builder are new (new_ prefix), just POST each one.
  const handleAdd = async (e) => {
    e.preventDefault();
    const { valid, errors: nextErrors } = validateAdd();
    if (!valid) {
      if (nextErrors.general) {
        showToast.error(nextErrors.general);
      }
      setTimeout(() => {
        const errorEl = document.querySelector('.field-error-msg');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    if (!(await validateImageDimensions(skus))) {
      showToast.error('One or more variant images have invalid dimensions');
      return;
    }

    const tid = showToast.loading(`Creating ${skus.length} variant${skus.length > 1 ? 's' : ''}…`);
    try {
      for (const s of skus) {
        const attrs = s.combo?.length
          ? s.combo.map(c => ({ key: c.key, value: c.value }))
          : (s.attributes || []).filter(a => a.key && a.value);

        const dimsObj = (s.shippingLength || s.shippingBreadth || s.shippingHeight) ? {
          length: s.shippingLength ? parseFloat(s.shippingLength) : null,
          breadth: s.shippingBreadth ? parseFloat(s.shippingBreadth) : null,
          height: s.shippingHeight ? parseFloat(s.shippingHeight) : null,
        } : null;

        await dispatch(createVariant({
          productId,
          variantName: s.variantName,
          mrp:         s.mrp,
          salesPrice:  s.salesPrice,
          stock:       s.stock,
          status:      s.status || 'Active',
          attributes:  attrs,
          sku:         s.sku,
          lowStockThreshold: s.lowStockThreshold,
          gstMode:     s.gstMode || 'Inclusive',
          gstRate:     normalizeGstRate(s.gstRate),
          shippingWeight: s.shippingWeight ? parseFloat(s.shippingWeight) : null,
          shippingDimensions: dimsObj,
          imageFile:   s.imageFile || null,
          galleryFiles: s.galleryPreviews?.map(g => g.file).filter(Boolean),
          galleryPreviews: s.galleryPreviews
        }));
      }
      showToast.success(`${skus.length} variant${skus.length > 1 ? 's' : ''} added!`, tid);
      closeForm();
      dispatch(fetchVariants());
    } catch (err) {
      showToast.error(err?.response?.data?.message || err?.message || 'Failed', tid);
    }
  };

  // ── Submit EDIT ───────────────────────────────────────────────────────────
  const handleEdit = async (e) => {
    e.preventDefault();
    const { valid, errors: nextErrors } = validateEdit();
    if (!valid) {
      if (nextErrors.general) {
        showToast.error(nextErrors.general);
      }
      if (nextErrors.sku) {
        showToast.error(nextErrors.sku);
      }
      setTimeout(() => {
        const errorEl = document.querySelector('.field-error-msg');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    if (editSku.imageFile) {
      const result = await validateProductImageDimensions(editSku.imageFile);
      if (!result.valid) {
        showToast.error(`Image: ${result.error}`);
        return;
      }
    }

    const tid = showToast.loading('Updating variant…');
    try {
      const attrs = (editSku.attributes || []).filter(a => a.key && a.value);
      const variantName = attrs.map(a => `${a.key}: ${a.value}`).join(' · ') || 'Default';

      const dimsObj = (editSku.shippingLength || editSku.shippingBreadth || editSku.shippingHeight) ? {
        length: editSku.shippingLength ? parseFloat(editSku.shippingLength) : null,
        breadth: editSku.shippingBreadth ? parseFloat(editSku.shippingBreadth) : null,
        height: editSku.shippingHeight ? parseFloat(editSku.shippingHeight) : null,
      } : null;

      const fallbackImage = editSku.imagePreview
        ? toRelativePath(editSku.imagePreview)
        : (editSku.galleryPreviews?.[0]?.path || (editSku.galleryPreviews?.[0]?.url ? toRelativePath(editSku.galleryPreviews[0].url) : null));

      await dispatch(editVariant({
        id: editingId,
        data: {
          productId,
          variantName,
          mrp:         editSku.mrp,
          salesPrice:  editSku.salesPrice,
          stock:       editSku.stock,
          status:      editSku.status || 'Active',
          attributes:  attrs,
          sku:         editSku.sku,
          lowStockThreshold: editSku.lowStockThreshold,
          gstMode:     editSku.gstMode || 'Inclusive',
          gstRate:     normalizeGstRate(editSku.gstRate),
          shippingWeight: editSku.shippingWeight ? parseFloat(editSku.shippingWeight) : null,
          shippingDimensions: dimsObj,
          image:       fallbackImage,
          imageFile:   editSku.imageFile instanceof File ? editSku.imageFile : null,
          galleryFiles: editSku.galleryPreviews?.map(g => g.file).filter(Boolean),
          galleryPreviews: editSku.galleryPreviews,
        },
      }));

      showToast.success('Variant updated!', tid);
      closeForm();
      dispatch(fetchVariants());
    } catch (err) {
      showToast.error(err?.response?.data?.message || err?.message || 'Failed', tid);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = (variantId) => {
    confirmDelete({
      title: 'Delete Variant?',
      message: 'Are you sure you want to delete this variant?',
      onConfirm: async () => {
        const tid = showToast.loading('Deleting variant...');
        try {
          await dispatch(removeVariant(variantId));
          showToast.success('Variant deleted successfully!', tid);
        } catch (err) {
          console.error('Failed to delete variant:', err);
          const errMsg = err?.response?.data?.message || err?.message || 'Failed to delete variant';
          showToast.error(errMsg, tid);
        }
      },
    });
  };

  const filtered = filterProduct ? rows.filter(r => String(r.productId) === filterProduct) : rows;
  const ErrorMsg = ({ field }) => errors[field]
    ? <div style={errorStyle}>⚠ {errors[field]}</div>
    : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Page header */}
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div className="section-title">Variants Management</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="search-input" value={filterProduct} onChange={e => setFilterProduct(e.target.value)} style={{ width: 180 }}>
            <option value="">All Products</option>
            {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {(hasPermission('variants_create') || mode !== 'none') && (
            <button className="action-btn btn-edit" style={{ padding: '7px 16px', fontSize: 13 }}
              onClick={mode !== 'none' ? closeForm : openAdd}>
              {mode !== 'none' ? '✕ Close' : '+ Add Variant'}
            </button>
          )}
        </div>
      </div>

      {/* ── ADD form ──────────────────────────────────────────────────────── */}
      {mode === 'add' && (
        <div style={formCard}>
          <div style={formHeader}>
            <div style={headerIcon}>+</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>Add New Variants</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>
                Select a product, define options, set prices & stock
              </div>
            </div>
          </div>

          <div style={{ padding: '20px 24px 24px' }}>
            <form onSubmit={handleAdd} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Product selector */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Product *</label>
                <select required style={inputStyle} value={productId} onChange={e => {
                  setProductId(e.target.value);
                  setSkus([]);
                }}>
                  <option value="">Select Product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name || p.productName}</option>)}
                </select>
               
              </div>

              {/* Variant Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {skus.map((sku, index) => {
                  const productExistingVariants = (rows || []).filter(r => String(r.productId) === String(productId));
                  const previousFormVariants = skus.slice(0, index).filter(s => s.attributes && s.attributes.some(a => a.value)).map((s, sIdx) => ({
                    id: `form_var_${sIdx}`,
                    variantName: `Variant ${sIdx + 1} (Current Form)`,
                    attributes: s.attributes
                  }));
                  const copyOptionsList = [...productExistingVariants, ...previousFormVariants];

                  return (
                    <div key={sku.id || index} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <VariantCard
                        index={index}
                        variant={sku}
                        showToast={showToast}
                        attributesSchema={getProductVariantSchema(products.find(p => String(p.id) === String(productId)))}
                        existingVariants={copyOptionsList}
                        onChange={(updated, idx) => {
                          const newSkus = [...skus];
                          newSkus[idx] = updated;
                          setSkus(newSkus);
                        }}
                        onRemove={skus.length > 1 ? (idx) => {
                          setSkus(skus.filter((_, i) => i !== idx));
                        } : null}
                        errors={errors.variantErrors?.[index]}
                        title={`Variant ${index + 1}`}
                      />

                      {/* Shipping Details for Variant */}
                      <div style={{ ...formCard, border: '1px solid #E0E7FF', marginTop: -10, marginBottom: 12 }}>
                        <div style={{ ...formHeader, background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ ...headerIcon, width: 26, height: 26, fontSize: 13 }}>📦</div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Shiprocket Logistics & Package Dimensions</div>
                              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Weight & dimensions for live courier rates (overrides product defaults)</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              copyProductShippingDetails(sku, (updatedVal) => {
                                const newSkus = [...skus];
                                newSkus[index] = updatedVal;
                                setSkus(newSkus);
                              });
                            }}
                            style={{
                              padding: '5px 12px',
                              background: 'rgba(255,255,255,0.2)',
                              border: '1px solid rgba(255,255,255,0.4)',
                              borderRadius: 6,
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              transition: 'background 0.2s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                          >
                            📋 Copy from Product
                          </button>
                        </div>
                        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Weight (kg)</label>
                            <input
                              style={inputStyle}
                              type="number" step="0.001" min="0.001"
                              placeholder="e.g. 0.5"
                              value={sku.shippingWeight || ''}
                              onChange={e => {
                                const newSkus = [...skus];
                                newSkus[index] = { ...sku, shippingWeight: e.target.value };
                                setSkus(newSkus);
                              }}
                            />
                            <span style={{ fontSize: 10, color: KM.muted }}>Package weight</span>
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Length (cm)</label>
                            <input
                              style={inputStyle}
                              type="number" step="0.1" min="0.5"
                              placeholder="e.g. 15"
                              value={sku.shippingLength || ''}
                              onChange={e => {
                                const newSkus = [...skus];
                                newSkus[index] = { ...sku, shippingLength: e.target.value };
                                setSkus(newSkus);
                              }}
                            />
                            <span style={{ fontSize: 10, color: KM.muted }}>Box length</span>
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Breadth (cm)</label>
                            <input
                              style={inputStyle}
                              type="number" step="0.1" min="0.5"
                              placeholder="e.g. 10"
                              value={sku.shippingBreadth || ''}
                              onChange={e => {
                                const newSkus = [...skus];
                                newSkus[index] = { ...sku, shippingBreadth: e.target.value };
                                setSkus(newSkus);
                              }}
                            />
                            <span style={{ fontSize: 10, color: KM.muted }}>Box width / breadth</span>
                          </div>
                          <div style={fieldStyle}>
                            <label style={labelStyle}>Height (cm)</label>
                            <input
                              style={inputStyle}
                              type="number" step="0.1" min="0.5"
                              placeholder="e.g. 8"
                              value={sku.shippingHeight || ''}
                              onChange={e => {
                                const newSkus = [...skus];
                                newSkus[index] = { ...sku, shippingHeight: e.target.value };
                                setSkus(newSkus);
                              }}
                            />
                            <span style={{ fontSize: 10, color: KM.muted }}>Box height</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* <button 
                type="button" 
                onClick={handleAddAnotherSku}
                style={{ padding: '10px 16px', background: '#f8fafc', border: `2px dashed ${KM.border}`, borderRadius: 8, color: KM.blue, fontWeight: 600, cursor: 'pointer' }}
              >
                + Add Another Variant
              </button> */}

              <ErrorMsg field="skus" />

              <button type="submit" style={submitBtn}>
                Save {skus.length > 0 ? `${skus.length} Variant${skus.length > 1 ? 's' : ''}` : 'Variants'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── EDIT form ─────────────────────────────────────────────────────── */}
      {mode === 'edit' && editSku && (
        <div style={formCard}>
          <div style={formHeader}>
            <div style={headerIcon}>✏</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>Edit Variant</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>
                Update options, price, stock, image and status
              </div>
            </div>
          </div>

          <div style={{ padding: '20px 24px 24px' }}>
            <form onSubmit={handleEdit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Product selector */}
              <div style={fieldStyle}>
                <label style={labelStyle}>Product *</label>
                <select required style={inputStyle} value={productId} onChange={e => setProductId(e.target.value)}>
                  <option value="">Select Product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name || p.productName}</option>)}
                </select>
                <ErrorMsg field="productId" />
              </div>

              {/* Variant Card for Edit */}
              <VariantCard
                variant={editSku}
                onChange={setEditSku}
                errors={errors}
                showToast={showToast}
                attributesSchema={getProductVariantSchema(products.find(p => String(p.id) === String(productId)))}
                existingVariants={(rows || []).filter(r => String(r.productId) === String(productId) && String(r.id) !== String(editSku.id))}
                isEditMode={true}
                title="Variant Details"
                subtitle="Update options, price, stock, GST and images"
              />

              {/* Shipping Details for Variant (Edit Mode) */}
              <div style={{ ...formCard, border: '1px solid #E0E7FF', marginTop: 12, marginBottom: 12 }}>
                <div style={{ ...formHeader, background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ ...headerIcon, width: 26, height: 26, fontSize: 13 }}>📦</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Shiprocket Logistics & Package Dimensions</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>Weight & dimensions for live courier calculations (overrides product defaults)</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyProductShippingDetails(editSku, setEditSku)}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(255,255,255,0.2)',
                      border: '1px solid rgba(255,255,255,0.4)',
                      borderRadius: 6,
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                  >
                    📋 Copy from Product
                  </button>
                </div>
                <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Weight (kg)</label>
                    <input
                      style={inputStyle}
                      type="number" step="0.001" min="0.001"
                      placeholder="e.g. 0.5"
                      value={editSku.shippingWeight || ''}
                      onChange={e => setEditSku({ ...editSku, shippingWeight: e.target.value })}
                    />
                    <span style={{ fontSize: 10, color: KM.muted }}>Package dead weight (in kg)</span>
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Length (cm)</label>
                    <input
                      style={inputStyle}
                      type="number" step="0.1" min="0.5"
                      placeholder="e.g. 15"
                      value={editSku.shippingLength || ''}
                      onChange={e => setEditSku({ ...editSku, shippingLength: e.target.value })}
                    />
                    <span style={{ fontSize: 10, color: KM.muted }}>Box length</span>
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Breadth (cm)</label>
                    <input
                      style={inputStyle}
                      type="number" step="0.1" min="0.5"
                      placeholder="e.g. 10"
                      value={editSku.shippingBreadth || ''}
                      onChange={e => setEditSku({ ...editSku, shippingBreadth: e.target.value })}
                    />
                    <span style={{ fontSize: 10, color: KM.muted }}>Box width / breadth</span>
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Height (cm)</label>
                    <input
                      style={inputStyle}
                      type="number" step="0.1" min="0.5"
                      placeholder="e.g. 8"
                      value={editSku.shippingHeight || ''}
                      onChange={e => setEditSku({ ...editSku, shippingHeight: e.target.value })}
                    />
                    <span style={{ fontSize: 10, color: KM.muted }}>Box height</span>
                  </div>
                </div>
              </div>



              <button type="submit" style={submitBtn}>Update Variant</button>
            </form>
          </div>
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      {loading ? (
        <p style={{ color: KM.muted, fontSize: 13 }}>Loading…</p>
      ) : (
        <DataTable
          columns={(() => {
            const cols = ['No.', 'Image', 'Product', 'Variant', 'MRP', 'Sale Price', 'Stock', 'SKU', 'Status'];
            if (hasPermission('variants_edit') || hasPermission('variants_delete')) cols.push('Actions');
            return cols;
          })()}
          initialRows={filtered}
          renderRow={(row, i) => (
            <tr key={row.id}>
              <td style={{ fontWeight: 600, color: KM.muted }}>{i + 1}</td>
              <td>
                {row.image ? (
                  <img src={getImgSrc(row.image)} alt={row.variantName}
                    width={40} height={40}
                    style={{ borderRadius: 8, objectFit: 'cover', border: `1px solid ${KM.border}` }} />
                ) : (
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: KM.bg, border: `1px solid ${KM.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: KM.muted }}>—</div>
                )}
              </td>
              <td style={{ fontWeight: 500 }}>{row.product?.name || row.productName || '—'}</td>
              <td style={{ fontWeight: 600 }}>{renderVariantLabel(row.variantName)}</td>
              <td>₹{parseFloat(row.mrp || 0).toLocaleString('en-IN')}</td>
              <td style={{ fontWeight: 700 }}>₹{parseFloat(row.salesPrice || 0).toLocaleString('en-IN')}</td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: stockColor(row.stock) }} />
                  {row.stock ?? 0}
                </div>
              </td>
              <td style={{ fontSize: 11, color: KM.muted, fontFamily: 'monospace' }}>{row.sku || '—'}</td>
              <td>
                <span className={`status-pill ${row.status === 'Active' ? 'pill-active' : 'pill-inactive'}`}>
                  {row.status}
                </span>
              </td>
              {(hasPermission('variants_edit') || hasPermission('variants_delete')) && (
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {hasPermission('variants_edit') && (
                      <button className="action-btn btn-edit" onClick={() => openEdit(row)}>Edit</button>
                    )}
                    {hasPermission('variants_delete') && (
                      <button className="action-btn btn-del"  onClick={() => handleDelete(row.id)}>Delete</button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          )}
        />
      )}
    </div>
  );
}
